import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { toast } from "sonner";
import { useDarkMode } from "../hooks/useDarkMode";
import { useNotificationSummary } from "../hooks/useNotificationSummary";
import { syncReportLanguage } from "../lib/reportLanguage";
import { navSectionsFor, activeNavItem, badgeText } from "../lib/nav";
import {
  HoverCard,
  HoverCardTrigger,
  HoverCardContent,
} from "./ui/hover-card";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "./ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "./ui/alert-dialog";
import {
  LogOut,
  Shield,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  X,
  Globe,
  Send,
  Loader2,
  Moon,
  Sun,
  HelpCircle,
  Keyboard,
  MoreHorizontal,
  MonitorX,
} from "lucide-react";

const API = "/api";

// The menu sections the user folded away, remembered in this browser. The
// section holding the open page is always shown, whatever is stored.
const CLOSED_SECTIONS_KEY = "sherlock_nav_closed_sections";

function readClosedSections() {
  try {
    const raw = JSON.parse(localStorage.getItem(CLOSED_SECTIONS_KEY) || "[]");
    return Array.isArray(raw) ? raw.filter((s) => typeof s === "string") : [];
  } catch {
    return [];
  }
}

const Badge = ({ count, testId }) => (
  <span
    className="ml-auto min-w-[20px] h-5 px-1.5 flex items-center justify-center rounded-full text-[11px] font-bold bg-amber-500 text-slate-950"
    data-testid={testId}
  >
    {badgeText(count)}
  </span>
);

// Layout owns the state: beside the page the sidebar is 256px or, collapsed,
// 72px; under the md breakpoint it is a drawer over the page (`drawer`),
// shown while `drawerOpen` and out of reach (inert) while closed. It is the
// same navy in light and dark mode, so its text takes no dark: variant.
const Sidebar = ({
  collapsed = false,
  onToggleCollapsed,
  drawer = false,
  drawerOpen = false,
  onCloseDrawer,
  onOpenHelp,
  onOpenShortcuts,
}) => {
  const { user, logout, logoutAll, isAdmin, isSuperAdmin } = useAuth();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { badges } = useNotificationSummary();
  const [exporting, setExporting] = useState(false);
  const [confirmAllOpen, setConfirmAllOpen] = useState(false);
  const [signingOutAll, setSigningOutAll] = useState(false);
  const [dark, setDark] = useDarkMode();
  const [closedSections, setClosedSections] = useState(readClosedSections);
  const activeLinkRef = useRef(null);

  const sections = useMemo(() => navSectionsFor(user), [user]);
  const active = useMemo(
    () => activeNavItem(pathname, sections.flatMap((s) => s.items)),
    [pathname, sections],
  );
  const activeSectionId = active
    ? sections.find((s) => s.items.includes(active))?.id
    : null;

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  // Every device, this one included. A failure keeps this browser signed in
  // and says so; a 401 means the session had already ended, and the 401
  // handler has signed this browser out.
  const handleLogoutAll = async () => {
    setSigningOutAll(true);
    try {
      await logoutAll();
      setConfirmAllOpen(false);
      toast.success(t("nav.signedOutEverywhere"));
      navigate("/login");
    } catch (err) {
      setConfirmAllOpen(false);
      if (err?.response?.status === 401) {
        toast.error(t("nav.sessionAlreadyEnded"));
        navigate("/login");
      } else {
        toast.error(t("nav.signOutAllFailed"));
      }
    } finally {
      setSigningOutAll(false);
    }
  };

  // Scheduled reports follow the language the app is used in.
  useEffect(() => {
    syncReportLanguage(user?.id || user?.user_id, i18n.language);
  }, [user, i18n.language]);

  // The open page's link is scrolled into the menu's view, so a page low in
  // a long admin menu is not hidden below the fold.
  useEffect(() => {
    const el = activeLinkRef.current;
    if (el && typeof el.scrollIntoView === "function") el.scrollIntoView({ block: "nearest" });
  }, [pathname, drawerOpen]);

  const toggleSection = useCallback((id) => {
    setClosedSections((prev) => {
      const next = prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id];
      try {
        localStorage.setItem(CLOSED_SECTIONS_KEY, JSON.stringify(next));
      } catch {
        // The choice just isn't remembered.
      }
      return next;
    });
  }, []);

  const toggleLang = () => {
    i18n.changeLanguage(i18n.language === "fr" ? "en" : "fr");
  };

  const handleExportMyData = async () => {
    setExporting(true);
    try {
      await axios.post(`${API}/auth/export/send-my-data`, {});
      toast.success(t("nav.dataSentToEmail"));
    } catch (err) {
      if (err?.response?.status === 401) {
        logout();
      } else {
        console.error("Export failed:", err);
        toast.error(t("nav.sendDataFailed"));
      }
    } finally {
      setExporting(false);
    }
  };

  const roleLabel = isSuperAdmin() ? t("roles.super_admin") : isAdmin() ? t("nav.administrator") : t("nav.notary");
  const initial = (user?.name || user?.email || "?").trim().charAt(0).toUpperCase();
  // Touch targets are 44px in the phone drawer, 40px beside the page.
  const rowPad = drawer ? "py-3" : "py-2.5";

  const renderItem = (item) => {
    const label = t(item.labelKey);
    const count = badges[item.to] || 0;
    const isActive = active?.to === item.to;
    const link = (
      <NavLink
        to={item.to}
        end
        ref={isActive ? activeLinkRef : undefined}
        onClick={drawer ? onCloseDrawer : undefined}
        title={collapsed ? label : undefined}
        aria-current={isActive ? "page" : undefined}
        data-testid={`nav-${item.to.slice(1).replace(/\//g, "-")}`}
        className={() =>
          `relative flex items-center gap-3 px-3 ${rowPad} rounded-lg transition-colors duration-150 group ${
            isActive
              ? "bg-cyan-600 text-white"
              : "text-slate-300 hover:text-white hover:bg-slate-800"
          } ${collapsed ? "justify-center" : ""}`
        }
      >
        <item.icon
          className={`flex-shrink-0 w-5 h-5 ${isActive ? "text-white" : "text-slate-400 group-hover:text-white"}`}
          aria-hidden="true"
        />
        {!collapsed && (
          <span className="text-sm font-medium whitespace-nowrap truncate">{label}</span>
        )}
        {!collapsed && count > 0 && <Badge count={count} testId={`nav-badge-${item.to.slice(1)}`} />}
        {collapsed && count > 0 && (
          <span className="absolute top-1.5 right-2 w-2.5 h-2.5 rounded-full bg-amber-500 ring-2 ring-[#0f172a]" aria-hidden="true" />
        )}
      </NavLink>
    );
    // Beside the page, hovering a link a moment shows what the page is for;
    // a phone has no hover, and the palette shows the same line.
    if (drawer || collapsed || !item.helpKey) return <li key={item.to}>{link}</li>;
    return (
      <li key={item.to}>
        <HoverCard openDelay={700} closeDelay={80}>
          <HoverCardTrigger asChild>
            <div>{link}</div>
          </HoverCardTrigger>
          <HoverCardContent side="right" align="start" sideOffset={12} className="w-72 p-3 bg-slate-900 border-slate-700 text-slate-200 shadow-xl break-words">
            <p className="text-xs font-semibold text-cyan-400 mb-1.5">{label}</p>
            <p className="text-xs leading-relaxed text-slate-300">{t(item.helpKey)}</p>
          </HoverCardContent>
        </HoverCard>
      </li>
    );
  };

  return (
    <aside
      id="app-sidebar"
      inert={drawer && !drawerOpen}
      aria-label={t("nav.mainMenu")}
      className={`flex flex-col h-[100dvh] fixed left-0 top-0 z-40 transition-[transform,width] duration-300 ${
        drawer && !drawerOpen ? "-translate-x-full" : "translate-x-0"
      } ${drawer && drawerOpen ? "shadow-2xl" : ""}`}
      data-testid="app-sidebar"
      style={{
        width: collapsed ? "72px" : "256px",
        background: "#0f172a",
        borderRight: "1px solid #1e293b",
      }}
    >
      {/* Logo */}
      <div
        className="flex items-center px-4 border-b flex-shrink-0"
        style={{ borderColor: "#1e293b", minHeight: "64px" }}
      >
        <div className="flex items-center gap-3 overflow-hidden">
          {collapsed ? (
            <div
              className="flex-shrink-0 w-9 h-9 rounded-lg flex items-center justify-center"
              style={{ background: "#171644" }}
            >
              <Shield className="w-5 h-5 text-cyan-300" />
            </div>
          ) : (
            <img
              src="/logos/logo-dark.png"
              alt="Audacia Corporate Services"
              className="h-9 object-contain"
            />
          )}
        </div>
        {drawer ? (
          <button
            type="button"
            onClick={onCloseDrawer}
            aria-label={t("nav.closeMenu")}
            className="ml-auto flex-shrink-0 w-11 h-11 -mr-2 flex items-center justify-center rounded-lg text-slate-400 hover:text-white hover:bg-slate-800 transition-colors"
            data-testid="sidebar-close"
          >
            <X className="w-5 h-5" />
          </button>
        ) : (
          <button
            type="button"
            onClick={onToggleCollapsed}
            aria-label={t(collapsed ? "nav.expandMenu" : "nav.collapseMenu")}
            title={t(collapsed ? "nav.expandMenu" : "nav.collapseMenu")}
            aria-expanded={!collapsed}
            className={`flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-md text-slate-400 hover:text-white hover:bg-slate-800 transition-colors ${collapsed ? "absolute -right-3 top-5 bg-slate-800 border border-slate-700" : "ml-auto"}`}
            data-testid="sidebar-toggle"
          >
            {collapsed ? (
              <ChevronRight className="w-4 h-4" />
            ) : (
              <ChevronLeft className="w-4 h-4" />
            )}
          </button>
        )}
      </div>

      {/* Navigation, in sections */}
      <nav className="flex-1 py-3 px-2 overflow-y-auto overscroll-contain" data-testid="sidebar-nav">
        {sections.map((section, idx) => {
          const open = collapsed || section.id === activeSectionId || !closedSections.includes(section.id);
          const hiddenCount = open ? 0 : section.items.reduce((n, i) => n + (badges[i.to] || 0), 0);
          return (
            <div key={section.id} className={idx > 0 ? (collapsed ? "mt-2 pt-2 border-t border-slate-800" : "mt-3") : ""} data-testid={`nav-section-${section.id}`}>
              {!collapsed && (
                <button
                  type="button"
                  onClick={() => toggleSection(section.id)}
                  aria-expanded={open}
                  disabled={section.id === activeSectionId}
                  className={`w-full flex items-center gap-2 px-3 ${drawer ? "py-2.5" : "py-1.5"} rounded-md text-[11px] font-semibold uppercase tracking-wider text-slate-500 hover:text-slate-300 disabled:hover:text-slate-500 disabled:cursor-default transition-colors`}
                  data-testid={`nav-section-toggle-${section.id}`}
                >
                  <span className="truncate">{t(section.labelKey)}</span>
                  {hiddenCount > 0 && <Badge count={hiddenCount} testId={`nav-section-badge-${section.id}`} />}
                  <ChevronDown
                    className={`w-3.5 h-3.5 flex-shrink-0 transition-transform ${hiddenCount > 0 ? "" : "ml-auto"} ${open ? "" : "-rotate-90"} ${section.id === activeSectionId ? "opacity-0" : ""}`}
                    aria-hidden="true"
                  />
                </button>
              )}
              {open && <ul className="space-y-0.5 mt-0.5">{section.items.map(renderItem)}</ul>}
            </div>
          );
        })}
      </nav>

      {/* The account: quick switches, then a menu for the rest */}
      <div className="px-2 py-3 border-t flex-shrink-0 space-y-1" style={{ borderColor: "#1e293b", paddingBottom: drawer ? "calc(0.75rem + env(safe-area-inset-bottom))" : undefined }}>
        {!collapsed && (
          <div className="flex items-center gap-1 px-1">
            <button
              type="button"
              onClick={toggleLang}
              data-testid="language-toggle"
              title={i18n.language === "fr" ? "English" : "Français"}
              aria-label={i18n.language === "fr" ? "English" : "Français"}
              className="flex-1 flex items-center justify-center gap-2 h-10 rounded-lg text-slate-300 hover:text-white hover:bg-slate-800 transition-colors text-xs font-semibold"
            >
              <Globe className="w-4 h-4" aria-hidden="true" />
              {i18n.language === "fr" ? "EN" : "FR"}
            </button>
            <button
              type="button"
              onClick={() => setDark((d) => !d)}
              data-testid="dark-mode-toggle"
              title={dark ? t("nav.lightMode") : t("nav.darkMode")}
              aria-label={dark ? t("nav.lightMode") : t("nav.darkMode")}
              className="flex-1 flex items-center justify-center h-10 rounded-lg text-slate-300 hover:text-white hover:bg-slate-800 transition-colors"
            >
              {dark ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            </button>
            <button
              type="button"
              onClick={onOpenHelp}
              data-testid="sidebar-help-btn"
              title={t("help.buttonLabel")}
              aria-label={t("help.buttonLabel")}
              className="flex-1 flex items-center justify-center h-10 rounded-lg text-slate-300 hover:text-white hover:bg-slate-800 transition-colors"
            >
              <HelpCircle className="w-4 h-4" />
            </button>
          </div>
        )}

        <DropdownMenu modal={false}>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              className={`w-full flex items-center gap-3 rounded-lg hover:bg-slate-800 transition-colors text-left ${collapsed ? "justify-center p-2" : "px-2 py-2"}`}
              data-testid="account-menu-btn"
              aria-label={t("nav.accountMenu")}
              title={collapsed ? user?.email : undefined}
            >
              <span className="flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold text-white" style={{ background: "#171644", boxShadow: "inset 0 0 0 1px #334155" }}>
                {initial}
              </span>
              {!collapsed && (
                <>
                  <span className="flex-1 min-w-0">
                    <span className="block text-white text-sm font-medium truncate">{user?.email}</span>
                    <span
                      className="inline-block mt-0.5 text-[11px] px-2 py-px rounded-full font-semibold"
                      style={{
                        background: isSuperAdmin() ? "#7c3aed" : isAdmin() ? "#312e81" : "#1e3a5f",
                        color: isSuperAdmin() ? "#ede9fe" : isAdmin() ? "#8dd9e2" : "#7dd3fc",
                      }}
                    >
                      {roleLabel}
                    </span>
                  </span>
                  <MoreHorizontal className="w-4 h-4 text-slate-400 flex-shrink-0" aria-hidden="true" />
                </>
              )}
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent side={collapsed ? "right" : "top"} align={collapsed ? "end" : "start"} className="w-60">
            <DropdownMenuLabel className="font-normal">
              <span className="block text-sm font-medium truncate">{user?.email}</span>
              <span className="block text-xs text-muted-foreground">{roleLabel}</span>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            {collapsed && (
              <>
                <DropdownMenuItem onSelect={toggleLang}>
                  <Globe className="w-4 h-4 mr-2" /> {i18n.language === "fr" ? "English" : "Français"}
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={() => setDark((d) => !d)}>
                  {dark ? <Sun className="w-4 h-4 mr-2" /> : <Moon className="w-4 h-4 mr-2" />}
                  {dark ? t("nav.lightMode") : t("nav.darkMode")}
                </DropdownMenuItem>
              </>
            )}
            <DropdownMenuItem onSelect={onOpenHelp} data-testid="account-help-item">
              <HelpCircle className="w-4 h-4 mr-2" /> {t("help.buttonLabel")}
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={onOpenShortcuts} data-testid="shortcuts-menu-item">
              <Keyboard className="w-4 h-4 mr-2" /> {t("shortcuts.title")}
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={handleExportMyData} disabled={exporting} data-testid="export-my-data-btn">
              {exporting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
              {exporting ? t("nav.sendingData") : t("nav.sendMyData")}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onSelect={handleLogout} data-testid="logout-btn" className="text-red-600 focus:text-red-700">
              <LogOut className="w-4 h-4 mr-2" /> {t("nav.signOut")}
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={() => setConfirmAllOpen(true)} data-testid="logout-all-btn" className="text-red-600 focus:text-red-700">
              <MonitorX className="w-4 h-4 mr-2" /> {t("nav.signOutAllDevices")}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        <AlertDialog open={confirmAllOpen} onOpenChange={(v) => { if (!signingOutAll) setConfirmAllOpen(v); }}>
          <AlertDialogContent className="w-[calc(100vw-1.5rem)] max-w-md rounded-xl sm:rounded-xl" data-testid="logout-all-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle>{t("nav.signOutAllTitle")}</AlertDialogTitle>
              <AlertDialogDescription>{t("nav.signOutAllBody")}</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter className="gap-2 sm:gap-0">
              <AlertDialogCancel disabled={signingOutAll} className="min-h-[44px] mt-0 sm:mt-0">{t("common.cancel")}</AlertDialogCancel>
              {/* A plain button, not AlertDialogAction: that one closes the
                  dialog on click, before the request has answered. */}
              <button
                type="button"
                onClick={handleLogoutAll}
                disabled={signingOutAll}
                data-testid="logout-all-confirm"
                className="inline-flex items-center justify-center gap-2 min-h-[44px] px-4 rounded-md text-sm font-semibold text-white bg-red-600 hover:bg-red-700 disabled:opacity-60 transition-colors"
              >
                {signingOutAll && <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />}
                {signingOutAll ? t("nav.signingOutAll") : t("nav.signOutAllConfirm")}
              </button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </aside>
  );
};

export default Sidebar;
